import type { CinematicId } from '@shefira/shared';

import type { ShakeKind } from '../screen-shake.js';
import { type ClipName, DRAGON_CLIPS } from './dragon-clips.js';

/**
 * Shefira's scenes (ADR 0067), as timelines: where she is and which frame
 * she shows, where her shadow lies and how big, when the screen shakes, what
 * is heard, when her name is written, how dark the world goes. Pure data,
 * read at a moment by `timeline.ts`, so a test (and the offline render that
 * checks the scene) reads them without Phaser; `CinematicPlayer` draws them.
 *
 * Everything is placed around one ground point `G`, a few tiles north of the
 * player where she lands, in world pixels: the dragon's canvas is laid with
 * its landed feet on `G` plus an offset, her shadow with its ground row on
 * `G` plus an offset. The player never moves (input is held), so the whole
 * scene sits in the world and the camera frames it.
 */

export interface Point {
  x: number;
  y: number;
}

export type Ease = 'linear' | 'in' | 'out' | 'inOut';

/** She plays one clip from `from` to `to`, her canvas sliding from `start` to `end` (offsets from landed, px). */
export interface DragonTrack {
  from: number;
  to: number;
  clip: ClipName;
  start: Point;
  end: Point;
  ease: Ease;
  /** Where the motion begins inside the track (the clip already plays before it). */
  moveFrom?: number;
}

/** Her flat shadow on the ground, from `from` to `to`: offset from `G`, whole-tenth scale, opacity. */
export interface ShadowTrack {
  from: number;
  to: number;
  start: Point & { scale: number; alpha: number };
  end: Point & { scale: number; alpha: number };
  ease: Ease;
}

export interface Cue {
  at: number;
  sound: 'sfx.dragon-roar' | 'sfx.dragon-wings' | 'sfx.dragon-rumble';
  volume: number;
}

export interface CinematicScript {
  ms: number;
  /** How far north of the player she lands, in world pixels. */
  north: number;
  dragon: readonly DragonTrack[];
  shadow: readonly ShadowTrack[];
  /** The shadow's own wing beat when no dragon is shown (ms per frame of `hover`). */
  shadowFrameMs: number;
  shakes: readonly { at: number; kind: ShakeKind }[];
  cues: readonly Cue[];
  /** Dust kicked up around her feet, offsets from `G`. */
  puffs: readonly { at: number; spread: number }[];
  title?: { from: number; to: number; text: string };
  /** How dark the world goes at most (0..1), fading in and out over `fadeMs`. */
  dusk: number;
  fadeMs: number;
  /** The camera leans north to frame her, eased in and out over `fadeMs` too. */
  frame: boolean;
}

const HOVER_MS = DRAGON_CLIPS.hover.ms;
const LANDING = DRAGON_CLIPS.landing;
const ROAR = DRAGON_CLIPS.roar;
const TAKEOFF = DRAGON_CLIPS.takeoff;
const IDLE = DRAGON_CLIPS.idle;

/** How high above the landing she enters, and leaves: her whole canvas off any view. */
const SKY = 420;

/** The breach's beats, in ms: each clip starts when the one before ends. */
const B = (() => {
  const descent = 2000;
  const landing = 3300;
  const idle = landing + LANDING.frames * LANDING.ms;
  const roar = idle + 6 * IDLE.ms;
  const rest = roar + ROAR.frames * ROAR.ms;
  const takeoff = rest + 4 * IDLE.ms;
  const climb = takeoff + TAKEOFF.frames * TAKEOFF.ms;
  return { descent, landing, idle, roar, rest, takeoff, climb, end: climb + 1500 };
})();

/** The moment her feet strike the ground in `landing` (frame 11). */
export const BREACH_IMPACT = B.landing + 11 * LANDING.ms;
/** Her roar held, jaws open and the fire up (frames 8 to 16 of `roar`). */
export const BREACH_ROAR = { from: B.roar + 8 * ROAR.ms, to: B.roar + 17 * ROAR.ms };
/** Her canvas rises out of the takeoff from its frame 6: the clip barely lifts her. */
const LIFT_AT = B.takeoff + 6 * TAKEOFF.ms;
const LIFT = 36;

/** Wing beats while she hovers: one per loop, on its downstroke (frame 1). */
function wingBeats(from: number, to: number, volume: number): Cue[] {
  const beats: Cue[] = [];
  for (let at = from + HOVER_MS; at < to; at += HOVER_MS * DRAGON_CLIPS.hover.frames) {
    beats.push({ at, sound: 'sfx.dragon-wings', volume });
  }
  return beats;
}

// La Ferme calcinée: her shadow alone, high over the fields, beating its
// wings; a roar far off, and the ground trembles.
const SHADOW_PASS: CinematicScript = {
  ms: 5200,
  north: 48,
  dragon: [],
  shadow: [
    {
      from: 200,
      to: 4800,
      // Her ground row runs 115 px under her body at this size: the body crosses over the player.
      start: { x: -560, y: 260, scale: 1.6, alpha: 0.3 },
      end: { x: 560, y: -120, scale: 1.6, alpha: 0.3 },
      ease: 'linear',
    },
  ],
  shadowFrameMs: 120,
  shakes: [
    { at: 1700, kind: 'rumble' },
    { at: 2700, kind: 'rumble' },
  ],
  cues: [
    { at: 600, sound: 'sfx.dragon-wings', volume: 0.35 },
    { at: 1560, sound: 'sfx.dragon-wings', volume: 0.35 },
    { at: 1500, sound: 'sfx.dragon-rumble', volume: 1 },
    { at: 2520, sound: 'sfx.dragon-wings', volume: 0.3 },
    { at: 3480, sound: 'sfx.dragon-wings', volume: 0.25 },
  ],
  puffs: [],
  dusk: 0.3,
  fadeMs: 700,
  frame: false,
};

export const CINEMATICS: Readonly<Record<CinematicId, CinematicScript>> = {
  'shefira-shadow': SHADOW_PASS,
  // Her flight from the Aire, wounded (ADR 0068 §4): a stand-in until its
  // own scene is written, her shadow passing over as at the burnt farm.
  'shefira-flight': SHADOW_PASS,
  // La Brèche: her shadow glides in, she comes down before the player,
  // lands, roars her name to the sky, and flies off north to the summits.
  'shefira-breach': {
    ms: B.end,
    north: 48,
    dragon: [
      {
        from: B.descent,
        to: B.landing,
        clip: 'hover',
        start: { x: 0, y: -SKY },
        end: { x: 0, y: 0 },
        ease: 'out',
      },
      {
        from: B.landing,
        to: B.idle,
        clip: 'landing',
        start: { x: 0, y: 0 },
        end: { x: 0, y: 0 },
        ease: 'linear',
      },
      {
        from: B.idle,
        to: B.roar,
        clip: 'idle',
        start: { x: 0, y: 0 },
        end: { x: 0, y: 0 },
        ease: 'linear',
      },
      {
        from: B.roar,
        to: B.rest,
        clip: 'roar',
        start: { x: 0, y: 0 },
        end: { x: 0, y: 0 },
        ease: 'linear',
      },
      {
        from: B.rest,
        to: B.takeoff,
        clip: 'idle',
        start: { x: 0, y: 0 },
        end: { x: 0, y: 0 },
        ease: 'linear',
      },
      {
        from: B.takeoff,
        to: B.climb,
        clip: 'takeoff',
        start: { x: 0, y: 0 },
        end: { x: 0, y: -LIFT },
        ease: 'in',
        moveFrom: LIFT_AT,
      },
      {
        from: B.climb,
        to: B.end - 300,
        clip: 'hover',
        start: { x: 0, y: -LIFT },
        end: { x: 0, y: -SKY - 60 },
        ease: 'in',
      },
    ],
    shadow: [
      // Glides in from behind the player, growing as she comes near...
      {
        from: 300,
        to: B.descent,
        start: { x: -40, y: 260, scale: 0.8, alpha: 0.15 },
        end: { x: 0, y: 0, scale: 1.5, alpha: 0.3 },
        ease: 'out',
      },
      // ...then shrinks to her size and darkens as she drops onto it...
      {
        from: B.descent,
        to: B.landing,
        start: { x: 0, y: 0, scale: 1.5, alpha: 0.3 },
        end: { x: 0, y: 0, scale: 1, alpha: 0.45 },
        ease: 'out',
      },
      // ...and melts under her as she lands.
      {
        from: B.landing,
        to: B.landing + 600,
        start: { x: 0, y: 0, scale: 1, alpha: 0.45 },
        end: { x: 0, y: 0, scale: 1, alpha: 0 },
        ease: 'linear',
      },
      // Off the ground again: it comes back under her and slides away north, growing faint.
      {
        from: LIFT_AT,
        to: B.climb,
        start: { x: 0, y: 0, scale: 1, alpha: 0 },
        end: { x: 0, y: -16, scale: 1.1, alpha: 0.4 },
        ease: 'linear',
      },
      {
        from: B.climb,
        to: B.end - 200,
        start: { x: 0, y: -16, scale: 1.1, alpha: 0.4 },
        end: { x: 0, y: -320, scale: 1.6, alpha: 0.05 },
        ease: 'in',
      },
    ],
    shadowFrameMs: HOVER_MS,
    shakes: [
      { at: 1400, kind: 'rumble' },
      { at: BREACH_IMPACT, kind: 'landing' },
      { at: BREACH_ROAR.from, kind: 'roar' },
      { at: BREACH_ROAR.from + 500, kind: 'roar' },
      { at: LIFT_AT, kind: 'rumble' },
    ],
    cues: [
      ...wingBeats(300, B.landing + 300, 0.55),
      { at: B.landing + 5 * LANDING.ms, sound: 'sfx.dragon-wings', volume: 0.8 },
      // The breath drawn as she rears, the roar let out as the jaws open.
      { at: B.roar + 5 * ROAR.ms, sound: 'sfx.dragon-roar', volume: 1 },
      { at: B.takeoff + 5 * TAKEOFF.ms, sound: 'sfx.dragon-wings', volume: 1 },
      ...wingBeats(B.climb, B.end - 600, 0.5),
    ],
    puffs: [{ at: BREACH_IMPACT, spread: 64 }],
    title: { from: BREACH_ROAR.from, to: B.takeoff + 300, text: 'SHEFIRA' },
    dusk: 0.4,
    fadeMs: 900,
    frame: true,
  },
};
